import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Redirect } from 'react-router';
import { useMutation } from 'react-apollo';
import pMap from 'p-map-browser';

import Button from '../global/Button';
import {
  removeSelectedRosterIdentity,
  resetFoundRosterIdentities,
} from '../../actions/discover-album-folder';
import { rosterIdentityLinkedIdentityCreate as rosterIdentityLinkedIdentityCreateMutation } from '../../graphql/mutations/roster-identity';
import IdentityAvatarContainer from '../../containers/global/identity/IdentityAvatarContainer';
import TrashIcon from '../icons/TrashIcon';
import OnboardingLayoutScrollableContent from '../../apps/temp/views/onboarding/OnboardingLayout/components/OnboardingLayoutScrollableContent';
import OnboardingLayoutFooter from '../../apps/temp/views/onboarding/OnboardingLayout/components/OnboardingLayoutFooter';
import WaldoSpeechBubble from '../global/WaldoSpeechBubble';
import useAlbumFolderForFaceRecognitionDisabled from '../global/hooks/useAlbumFolderForFaceRecognitionDisabled';
import { PRICING_CATEGORY_TYPE } from '../../constants/pricing-categories';
import sleep from '../../util/sleep';

const propTypes = {
  albumFolderId: PropTypes.string,
};

const DiscoverAlbumFolderNoFrJoinConfirm = ({ albumFolderId }) => {
  const { albumFolder } = useAlbumFolderForFaceRecognitionDisabled({
    albumFolderId,
  });
  const history = useHistory();
  const dispatch = useDispatch();
  const [isJoining, setIsJoining] = useState(false);

  const [rosterIdentityLinkedIdentityCreate] = useMutation(
    rosterIdentityLinkedIdentityCreateMutation,
  );

  const selectedRosterIdentities = useSelector(
    state => state.discoverAlbumFolder.selectedRosterIdentities,
  );

  const isSchoolAndAcademics = (
    albumFolder?.pricing_categories?.records ?? []
  ).some(
    r =>
      r?.pricing_category?.type === PRICING_CATEGORY_TYPE.SCHOOL_AND_ACADEMICS,
  );
  const subjectTerm = isSchoolAndAcademics ? 'student' : 'member';

  if (!selectedRosterIdentities || selectedRosterIdentities.length === 0) {
    return (
      <Redirect to={`/discover/album-folder/${albumFolderId}/no-fr-join`} />
    );
  }

  const onRemove = rosterIdentity => {
    dispatch(removeSelectedRosterIdentity(rosterIdentity));
  };

  const onAddAnother = () => {
    dispatch(resetFoundRosterIdentities());
    history.push(`/discover/album-folder/${albumFolderId}/no-fr-join`);
  };

  const onJoin = async () => {
    setIsJoining(true);
    try {
      await pMap(selectedRosterIdentities, async rosterIdentity =>
        rosterIdentityLinkedIdentityCreate({
          variables: {
            input: {
              roster_identity_id: rosterIdentity.id,
              hierarchy_item_id: albumFolderId,
            },
          },
        }),
      );
      // Add wait time to allow the ID to join the event before redirecting to matches
      await sleep(5000);
      history.push(`/consumer/matches/${albumFolderId}`);
    } finally {
      setIsJoining(false);
    }
  };

  return (
    <Fragment>
      <WaldoSpeechBubble position="right" className="mb-4">
        Tap join the event below if this is your only {subjectTerm} or add
        another {subjectTerm} below
      </WaldoSpeechBubble>
      <OnboardingLayoutScrollableContent className="w-100">
        <div className="no-fr-join-confirm-identity-container">
          {selectedRosterIdentities.map(rosterIdentity => {
            const albumFolderLink = (
              rosterIdentity?.links?.records ?? []
            )?.find(r => r.album_folder?.id === albumFolderId);

            return (
              <div key={rosterIdentity.id} className="item">
                <div className="w-100 d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <IdentityAvatarContainer
                      className="mr-2"
                      identity={{
                        ...rosterIdentity,
                        current_photo: rosterIdentity?.selfie_photo,
                      }}
                    />
                    <div className="d-flex flex-column">
                      <span className="text-dark text-extra-bold">
                        {rosterIdentity.first_name} {rosterIdentity.last_name}
                      </span>
                      {albumFolderLink?.ref_id && (
                        <span className="text-xs text-muted">
                          ID ending in {albumFolderLink.ref_id.slice(-3)}
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    btnStyle="reset"
                    type="button"
                    className="ml-auto d-flex"
                    onClick={() => onRemove(rosterIdentity)}
                  >
                    <TrashIcon size="15" color="#FE5851" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
        <div className="text-center">
          <Button
            btnStyle="link"
            className="mt-2"
            onClick={onAddAnother}
            disabled={isJoining}
          >
            Add another {subjectTerm}
          </Button>
        </div>
      </OnboardingLayoutScrollableContent>
      <OnboardingLayoutFooter>
        <Button
          btnSize="lg"
          className="mt-3"
          onClick={onJoin}
          disabled={isJoining}
        >
          {isJoining ? 'Joining...' : 'Join the event'}
        </Button>
      </OnboardingLayoutFooter>
    </Fragment>
  );
};

DiscoverAlbumFolderNoFrJoinConfirm.propTypes = propTypes;

export default DiscoverAlbumFolderNoFrJoinConfirm;
